import React, { useState } from 'react';
import { CreateFormFeatureDialog } from 'components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { PIPELINES_AND_STEPS_PATHS } from 'constants/pipelines-and-steps-paths';
import { useCreatePipeline, usePipelinesList } from 'hooks';
import { useAlert, useAuth, useServicesEnabled } from '../../../core';
import {
    CreatePipelineFormData,
    CreatePipelineSchema,
    CreatePipelineDefaults,
    createPipelineConfig,
    createPipelineStyles,
    checkForDuplicatePipeline,
} from './create-or-update-pipeline-helper';
import { PipelineForm } from './components/PipelineForm';
import { commonShowAlert } from 'lib';

export const CreatePipeline = (): React.JSX.Element => {
    const navigate = useNavigate();
    const { isServiceEnabledFor } = useServicesEnabled();
    const isDataPipelineServiceEnabled = isServiceEnabledFor('DATA_PIPELINE');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<CreatePipelineFormData>({
        mode: 'all',
        resolver: yupResolver(CreatePipelineSchema),
        defaultValues: CreatePipelineDefaults,
    });
    const { getToken } = useAuth();
    const { data: pipelines } = usePipelinesList(isDataPipelineServiceEnabled);
    const { mutate: createPipeline } = useCreatePipeline();
    const { showAlert, hideAlert } = useAlert();

    const onSubmit = (data: CreatePipelineFormData): void => {
        setIsLoading(true);
        let isDuplicatePipeline = false;
        isDuplicatePipeline = checkForDuplicatePipeline(
            pipelines?.data ?? [],
            data
        );
        if (isDuplicatePipeline) {
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'A pipeline with this name already exists.'
            );
            setIsLoading(false);
            return;
        }
        const postData = {
            name: data.name,
            description: data.description ? data.description : '',
            stepIds: [],
        };
        createPipeline(
            { postData, token: getToken() },
            {
                onSuccess: (result: any) => {
                    setIsLoading(false);
                    navigate(
                        PIPELINES_AND_STEPS_PATHS.PIPELINE_DETAILS.replace(
                            ':id',
                            result.data.id
                        )
                    );
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const handleCloseOrBackButtonClick = (): void => {
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
    };

    if (!isDataPipelineServiceEnabled) {
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        return <></>;
    }

    return (
        <CreateFormFeatureDialog
            buttonText='Save'
            isLoading={isLoading}
            handleSubmit={handleSubmit(onSubmit)}
            handleCloseButton={handleCloseOrBackButtonClick}
            handleBackButton={handleCloseOrBackButtonClick}
            listConfig={createPipelineConfig}
            customStyles={createPipelineStyles.root}
        >
            <PipelineForm
                control={control}
                errors={errors}
                pipelineFormValues={CreatePipelineDefaults}
                isEdit={false}
            />
        </CreateFormFeatureDialog>
    );
};
